import type { CityProgress } from "../../../../api/dispatchSupabaseApi";
import { P_THRESHOLD } from "../../../../api/dispatchSupabaseApi";

interface CityProgressCardsProps {
  cityProgress: CityProgress[];
}

function ProgressBar({ value, max }: { value: number; max: number }) {
  const pct = Math.min((value / max) * 100, 100);
  const isReady = value >= max;
  return (
    <div className="w-full h-2 bg-[#F7F6F3] rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${isReady ? "bg-emerald-400" : "bg-[#C8B8A6]"}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function StatusBadgeLocal({ isReady }: { isReady: boolean }) {
  if (isReady) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-light bg-emerald-50 text-emerald-600">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        Ready
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-light bg-[#C8B8A6]/15 text-[#2C2C2C]/50">
      <span className="w-1.5 h-1.5 rounded-full bg-[#C8B8A6]" />
      Collecting
    </span>
  );
}

export default function CityProgressCards({ cityProgress }: CityProgressCardsProps) {
  if (cityProgress.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-[#D9D9D9]/60 py-10 flex flex-col items-center gap-2 text-center">
        <div className="w-8 h-8 flex items-center justify-center text-[#2C2C2C]/20">
          <i className="ri-map-pin-line text-2xl" />
        </div>
        <p className="text-sm text-[#2C2C2C]/30 font-light">No unbatched orders found across any city</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 xl:grid-cols-3">
      {cityProgress.map((cp) => {
        const remaining = Math.max(P_THRESHOLD - cp.total_p, 0);
        return (
          <div
            key={cp.city}
            className={`bg-white rounded-lg border p-5 transition-all ${
              cp.is_ready
                ? "border-emerald-200 bg-emerald-50/30"
                : "border-[#D9D9D9]/60"
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-sm font-light text-[#2C2C2C]">{cp.city}</h3>
                <p className="text-[11px] text-[#2C2C2C]/40 mt-0.5">
                  {cp.order_count} order{cp.order_count !== 1 ? "s" : ""}
                </p>
              </div>
              <StatusBadgeLocal isReady={cp.is_ready} />
            </div>

            <div className="mb-3">
              <div className="flex items-end justify-between mb-1.5">
                <span className="text-2xl font-light text-[#2C2C2C]">
                  {cp.total_p}
                  <span className="text-sm text-[#2C2C2C]/40 ml-1">/ {P_THRESHOLD} P</span>
                </span>
              </div>
              <ProgressBar value={cp.total_p} max={P_THRESHOLD} />
            </div>

            {!cp.is_ready && (
              <p className="text-[11px] text-[#2C2C2C]/40 font-light">
                Remaining: <strong className="text-[#2C2C2C]/60">{cp.remaining_p} P</strong> to reach threshold
              </p>
            )}
            {cp.is_ready && (
              <p className="text-[11px] text-emerald-600 font-light">
                Threshold reached — ready to dispatch
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
